import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Select, Table, Button, message } from 'antd'
import Layout from '@components/Layout'
import { useQuery } from '@tanstack/react-query'
import { http } from '@services/http'
import { formatInputNumber, removeSpaces } from '@utils/numberWithSpaces'
import EditIcon from '@assets/icons/EditSvg'
import { catchError } from '@utils/errrorHandling'
import SuccessModal from '@Modal/SuccessModal'
import ErrorModal from '@Modal/ErrorModal'
import { render } from '@testing-library/react'
import { debouncedSet } from '@utils/debounce'

const ItemsPriceForShapes = () => {
	const { t } = useTranslation()
	const [currentPage, setCurrentPage] = useState(0)
	const [itemName, setItemName] = useState('')
	const [inputItemName, setInputItemName] = useState('')
	const [itemsData, setItemsData] = useState([])
	const [hasMore, setHasMore] = useState(true)
	const [btnLoading, setBtnLoading] = useState(false)
	const [activeTab, setActiveTab] = useState('tab1')
	const [tabType, setTabType] = useState('Rombik')

	const successRef = useRef()
	const errorRef = useRef()
	const tableContainerRef = useRef(null)

	const tabOptions = [
		{ key: 'Rombik', label: t('Rombik') },
		{ key: 'Kalit', label: t('Kalit') },
	]

	// API fetch function
	const fetchItems = async ({ queryKey }) => {
		const [_, tabType, itemName, page] = queryKey
		const url = `/api/items/price-of-shapes?PageSize=30&Skip=${page}&TabType=${tabType}&ItemName=${itemName}`
		const { data } = await http.get(url)
		return data
	}

	const {
		data = [],
		isLoading,
		isFetching,
		refetch,
	} = useQuery({
		queryKey: ['itemsForShapes', tabType, itemName, currentPage],
		queryFn: fetchItems,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	useEffect(() => {
		if (data?.length > 0) {
			setItemsData((prev) =>
				currentPage === 0 ? data : [...prev, ...data],
			)
			setHasMore(data.length === 30)
		} else {
			setHasMore(false)
		}
	}, [data, currentPage])

	const handleScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target
		const isBottom = scrollHeight - scrollTop <= clientHeight + 100
		if (isBottom && hasMore && !isLoading) {
			setCurrentPage((prev) => prev + 1)
		}
	}

	const handleFilterChange = (setter, inputSetter) => (e) => {
		const value = e.target ? e.target.value : e
		inputSetter(value)
		debouncedSet(value, setter)
		setCurrentPage(0) // Reset to first page on filter change
		setItemsData([])
	}

	const handleTabChange = (key) => {
		setTabType(key)
		setInputItemName('')
		setItemName('')
		setCurrentPage(0)
		setItemsData([])
	}

	const handleSave = async () => {
		setBtnLoading(true)
		const body = itemsData
			?.filter((item) => item.isPatchData)
			?.map((item) => ({
				code: item.Code,
				uItemCode: item.U_ItemCode,
				uItemName: item.U_ItemName,
				uServiceType: item.U_ServiceType || '',
				uSizeType: item.U_SizeType || '',
				uTabType: item.U_TabType || '',
				uExchange: item.U_Exchange,
				uPrice: removeSpaces(item.U_Price),
			}))
		try {
			await http.patch(`/api/items/price-of-shapes`, body)
			// setItemsData([])
			setCurrentPage(0)
			refetch()
			successRef.current.open(t('sucCreated'))
		} catch (error) {
			catchError(error, errorRef)
		} finally {
			setBtnLoading(false)
		}
	}

	const columns = [
		{
			title: t('serviceCode'),
			dataIndex: 'U_ItemCode',
			key: 'U_ItemCode',
			align: 'center',
		},
		{
			title: t('serviceName'),
			dataIndex: 'U_ItemName',
			key: 'U_ItemName',
			align: 'center',
		},
		{
			title: tabType === 'Rombik' ? t('serviceType') : t('sizeType'),
			dataIndex: 'U_ServiceType',
			key: 'U_ServiceType',
			align: 'center',
			render: (text, record, index) => (
				<span>{tabType === 'Rombik' ? text : record.U_SizeType}</span>
			),
		},
		{
			title: t('currency'),
			dataIndex: 'U_Exchange',
			key: 'U_Exchange',
			align: 'center',
			render: (text, record, index) =>
				record.isEditing ? (
					<Select
						value={text}
						className="w-[150px]"
						options={[
							{ value: 'USD', label: 'USD' },
							{ value: 'UZS', label: 'UZS' },
						]}
						onChange={(val) => {
							const newData = [...itemsData]
							newData[index] = { ...record, U_Exchange: val }
							setItemsData(newData)
						}}
					/>
				) : (
					<span>{text}</span>
				),
		},
		{
			title: t('price'),
			dataIndex: 'U_Price',
			key: 'U_Price',
			align: 'center',
			render: (text, record, index) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return record.isEditing ? (
					<Input
						value={formatInputNumber(text)}
						className="w-[150px]"
						onChange={(e) => {
							const newData = [...itemsData]
							newData[index] = {
								...record,
								U_Price: removeSpaces(e.target.value),
							}
							setItemsData(newData)
						}}
					/>
				) : (
					<span>{formattedText}</span>
				)
			},
		},
		{
			title: '',
			key: 'action',
			align: 'center',
			width: 100,
			render: (_, record, index) => (
				<button
					onClick={() => {
						const newData = [...itemsData]
						newData[index] = {
							...record,
							isEditing: record.isEditing ? false : true,
							isPatchData: record.isPatchData ? false : true,
						}
						setItemsData(newData)
					}}
					disabled={!record.U_ItemCode}
					className="bg-[#1d5870] text-white w-[30px] h-[30px] rounded-lg flex justify-center items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
				>
					<EditIcon />
				</button>
			),
		},
	]

	return (
		<Layout>
			<div className="m-5 border-[2px] border-gray-200 rounded-lg">
				<div className="m-5">
					<div className="flex  justify-between items-center  mr-10 mt-6">
						<h1 className="font-poppins text-xl font-bold text-black">
							{t('itemsPriceForShapes')}
						</h1>
					</div>

					<div className="flex justify-between items-center  mt-6">
						<div className="flex gap-2 ">
							{tabOptions.map((tab) => (
								<Button
									key={tab.key}
									type={
										tabType === tab.key
											? 'primary'
											: 'default'
									}
									disabled={btnLoading}
									onClick={() => handleTabChange(tab.key)}
									className={
										tabType === tab.key
											? 'bg-[#1d5870]'
											: ''
									}
								>
									{t(tab.label)}
								</Button>
							))}
						</div>
						<Button
							onClick={handleSave}
							loading={btnLoading}
							disabled={itemsData.every(
								(item) => !item.isPatchData,
							)}
							className="bg-[#1d5870] text-white"
						>
							{t('save')}
						</Button>
					</div>

					<div className="flex gap-4 mt-6">
						<Input.Search
							placeholder={t('serviceName')}
							value={inputItemName}
							onChange={handleFilterChange(
								setItemName,
								setInputItemName,
							)}
							className="w-[300px]"
						/>
					</div>

					{/* Filter Inputs */}
					{/* <div className="flex gap-4 mx-10 mt-6">
					<Input
						placeholder={t('itemName')}
						value={itemName}
						onChange={handleFilterChange(setItemName)}
						className="w-[200px]"
					/>
					<Input
						placeholder={t('itemCode')}
						value={itemCode}
						onChange={handleFilterChange(setItemCode)}
						className="w-[200px]"
					/>
					<Input
						placeholder={t('category')}
						value={category}
						onChange={handleFilterChange(setCategory)}
						className="w-[200px]"
					/>
					<Select
						placeholder={t('itemType')}
						value={itemType || undefined}
						onChange={handleFilterChange(setItemType)}
						className="w-[200px]"
						allowClear
						options={[
							{ value: 'type1', label: 'Type 1' },
							{ value: 'type2', label: 'Type 2' },
						]}
					/>
				</div> */}

					{/* Table */}
					<div
						ref={tableContainerRef}
						onScroll={handleScroll}
						className="my-5"
						style={{
							height: '70vh',
							overflowY: 'auto',
						}}
					>
						<Table
							columns={columns}
							dataSource={itemsData}
							pagination={false}
							bordered
							rowKey="Code"
							loading={isLoading || isFetching}
							sticky
						/>
						{/* {isLoading && (
						<div className="text-center p-3 text-gray-500">
							{t('loading') || 'Loading...'}
						</div>
					)} */}
					</div>
				</div>
			</div>
			<SuccessModal
				getRef={(r) => {
					successRef.current = r
				}}
			/>
			<ErrorModal
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
		</Layout>
	)
}

export default ItemsPriceForShapes
